import { AlertCircle, Briefcase, CheckCircle, Loader, Sparkles, TrendingUp } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import LoadingOverlay from '../Common/LoadingOverlay';
import './JobDescriptionAnalyzer.css';

interface JobAnalysis {
  keywords: string[];
  requirements: {
    technical: string[];
    soft: string[];
    experience: string[];
  };
  gaps: string[];
  matchScore: number;
  suggestions: string[];
}

const JobDescriptionAnalyzer: React.FC = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams<{ resumeId: string }>();
  
  const [jobDescription, setJobDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<JobAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!jobDescription.trim()) {
      setError('Please paste a job description');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const result = await api.analyzeJobDescription(resumeId!, jobDescription);
      setAnalysis(result);
    } catch (err: any) {
      console.error('Job analysis failed:', err);
      setError(err.response?.data?.error || 'Failed to analyze job description. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleOptimize = () => {
    if (!analysis || !analysis.suggestions || analysis.suggestions.length === 0) return;

    // Store optimization suggestions in sessionStorage
    sessionStorage.setItem('optimization_suggestions', JSON.stringify(analysis.suggestions));
    
    // Navigate to editor
    navigate(`/resume/${resumeId}/edit`);
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getMatchScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    return 'Needs Improvement';
  };

  return (
    <div className="job-analyzer">
      {isAnalyzing && <LoadingOverlay message="Analyzing job description..." />}
      
      <div className="analyzer-header">
        <div className="header-icon-circle">
          <Briefcase size={32} />
        </div>
        <div className="header-text">
          <h1 className="header-title">Job Description Analyzer</h1>
          <p className="header-description">
            Paste a job posting to see how well your resume matches and get AI-powered suggestions
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Job Description Input */}
      <div className="analyzer-section">
        <label htmlFor="job-description" className="section-label">
          Job Description
        </label>
        <textarea
          id="job-description"
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          placeholder="Paste the full job description here...

Example:
Job Title: Senior Software Engineer
Company: TechCorp

We're looking for an experienced software engineer with 5+ years of experience in React, Node.js, and AWS. You'll be responsible for..."
          className="job-description-input"
          rows={12}
          disabled={isAnalyzing}
        />
        <button
          onClick={handleAnalyze}
          disabled={!jobDescription.trim() || isAnalyzing}
          className="btn btn-primary btn-analyze"
        >
          {isAnalyzing ? (
            <>
              <Loader size={18} className="spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles size={18} />
              Analyze Match
            </>
          )}
        </button>
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div className="analysis-results">
          {/* Match Score */}
          <div className="result-card score-card">
            <div className="card-header">
              <TrendingUp size={20} />
              <h3>Match Score</h3>
            </div>
            <div className="score-display">
              <div className={`score-value ${getMatchScoreColor(analysis.matchScore)}`}>
                {analysis.matchScore}%
              </div>
              <div className="score-label">
                {getMatchScoreLabel(analysis.matchScore)}
              </div>
            </div>
            <div className="score-bar">
              <div
                className={`score-fill ${getMatchScoreColor(analysis.matchScore)}`}
                style={{ width: `${analysis.matchScore}%` }}
              />
            </div>
          </div>

          {/* Keywords */}
          <div className="result-card">
            <div className="card-header">
              <CheckCircle size={20} />
              <h3>Key Skills & Keywords</h3>
            </div>
            <div className="keywords-list">
              {analysis.keywords.map((keyword, index) => (
                <span key={index} className="keyword-tag">
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div className="result-card">
            <div className="card-header">
              <Briefcase size={20} />
              <h3>Job Requirements</h3>
            </div>
            <div className="requirements-grid">
              {analysis.requirements.technical.length > 0 && (
                <div className="requirement-group">
                  <h4>Technical Skills</h4>
                  <ul>
                    {analysis.requirements.technical.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.requirements.soft.length > 0 && (
                <div className="requirement-group">
                  <h4>Soft Skills</h4>
                  <ul>
                    {analysis.requirements.soft.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.requirements.experience.length > 0 && (
                <div className="requirement-group">
                  <h4>Experience Requirements</h4>
                  <ul>
                    {analysis.requirements.experience.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Gaps */}
          {analysis.gaps.length > 0 && (
            <div className="result-card gaps-card">
              <div className="card-header">
                <AlertCircle size={20} />
                <h3>Missing from Your Resume</h3>
              </div>
              <ul className="gaps-list">
                {analysis.gaps.map((gap, index) => (
                  <li key={index} className="gap-item">
                    <AlertCircle size={16} />
                    <span>{gap}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <div className="result-card suggestions-card">
              <div className="card-header">
                <Sparkles size={20} />
                <h3>AI Recommendations</h3>
              </div>
              <div className="suggestions-list">
                {analysis.suggestions.map((suggestion, index) => (
                  <div key={index} className="suggestion-item">
                    <div className="suggestion-number">{index + 1}</div>
                    <p>{suggestion}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimize Button */}
          <div className="analyzer-actions">
            <button
              onClick={handleOptimize}
              className="btn btn-primary btn-lg"
              disabled={isAnalyzing}
            >
              <Sparkles size={20} />
              Optimize Resume for This Job
            </button>
            <p className="action-hint">
              AI will suggest specific changes to improve your match score
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDescriptionAnalyzer;

